/* eslint-disable max-lines -- one component composing the toolbar, header, body, and footer of
   DimeDataTable, including forwarding every pagination-footer prop through to
   DataTablePagination; splitting the forwarding out would just fragment one cohesive prop list
   across files without a readability gain. */
import { flexRender, type RowData, useTable } from '@tanstack/react-table';
import * as React from 'react';

import { DimeButton } from '../DimeButton.js';
import {
  DimeTable,
  DimeTableBody,
  DimeTableCell,
  DimeTableHead,
  DimeTableHeader,
  DimeTableRow,
  SkeletonRows,
} from '../DimeTable/index.js';
import { useIsNarrowerThan } from '../lib/use-is-narrower-than.js';
import { cn } from '../lib/utils.js';
import { getAriaSort } from '../table-internals/get-aria-sort.js';
import { useBoundedTableHeight } from '../table-internals/use-bounded-table-height.js';
import type { CardSortTable } from './card-sort-table.js';
import { DataTableCardSort } from './DataTableCardSort.js';
import { DataTableExport } from './DataTableExport.js';
import { DataTablePagination } from './DataTablePagination.js';
import { DataTableToolbar } from './DataTableToolbar.js';
import type {
  ColumnDef,
  ColumnFiltersState,
  DimeDataTableProps,
  SortingState,
  VisibilityState,
} from './DimeDataTable.types.js';
import type { ExportableColumn, ExportTable } from './export-table.js';
import { MOBILE_ROW_MAX_WIDTH } from './mobile-row-max-width.js';
import { RowGroupToggle } from './RowGroupToggle.js';
import { buildSelectionColumn } from './selection-column.js';
import { dimeTableFeatures, type DimeTableFeatures } from './TableFeatures.js';
import { useRowGroups } from './use-row-groups.js';

export type { DimeDataTableProps } from './DimeDataTable.types.js';

// Uses row.id for stable cross-page selection; String(index) is page-local only — supply getRowId when rows lack id.
function defaultGetRowId(row: unknown, index: number): string {
  const id = (row as { id?: unknown } | null)?.id;
  return typeof id === 'string' || typeof id === 'number' ? String(id) : String(index);
}

// Mirrors TanStack's constructColumn id rule, which throws on a column with none of id, accessorKey or a string header.
function columnIdOf(column: { id?: string; accessorKey?: unknown; header?: unknown }): string {
  if (column.id !== undefined) return column.id;
  if (column.accessorKey !== undefined) return String(column.accessorKey).replaceAll('.', '_');
  return String(column.header);
}

export function DimeDataTable<TData extends RowData, TValue>({
  columns,
  data,
  hasNextPage = false,
  hasPreviousPage = false,
  onNextPage,
  onPreviousPage,
  onFirstPage,
  onLastPage,
  showFirstLast = true,
  pageIndex,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  totalCount,
  sortBy,
  onUpdateSortBy,
  manualSorting = false,
  searchColumn,
  onVisibleRowCountChange,
  searchPlaceholder,
  searchMaxLength,
  onSearchChange,
  searchValue,
  filters,
  actions,
  showViewOptions = true,
  viewOptionsMenuClassName,
  showExport = true,
  exportDisabledReason,
  exportFileName = 'export',
  onRequestExportRows,
  exportConfirmDialog,
  filterChips,
  columnFilters,
  onColumnFiltersChange,
  manualFiltering = false,
  onRowClick,
  renderSubRow,
  getRowGroupId,
  rowGroupsExpanded = false,
  rowGroupNoun,
  rowGroupToggleLabel,
  renderMobileRowGroupHeader,
  showSelect = false,
  showSelectAll = true,
  selectAll,
  selected = {},
  onUpdateSelected,
  canSelectRow,
  getRowId,
  columnVisibility,
  onColumnVisibilityChange,
  isLoading = false,
  loadingRowCount = 20,
  emptyMessage = 'No results.',
  showHeaders = true,
  className,
  striped = false,
  maxBodyHeight,
  getRowClassName,
  renderMobileRow,
}: Readonly<DimeDataTableProps<TData, TValue>>) {
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>([]);
  // Lazy, so defaultHidden seeds once and never fights a person re-showing the column.
  const [internalVisibility, setInternalVisibility] = React.useState<VisibilityState>(() =>
    Object.fromEntries(
      columns
        .filter((column) => column.meta?.defaultHidden)
        .map((column) => [columnIdOf(column), false]),
    ),
  );

  const resolvedSorting = sortBy ?? internalSorting;
  const resolvedOnSortingChange = onUpdateSortBy ?? setInternalSorting;
  const resolvedColumnFilters = columnFilters ?? internalColumnFilters;
  const resolvedOnColumnFiltersChange = onColumnFiltersChange ?? setInternalColumnFilters;
  const resolvedVisibility = columnVisibility ?? internalVisibility;
  const resolvedOnVisibilityChange = onColumnVisibilityChange ?? setInternalVisibility;

  const resolvedColumns = React.useMemo(
    () =>
      showSelect ? [buildSelectionColumn<TData>(showSelectAll, selectAll), ...columns] : columns,
    [columns, showSelect, showSelectAll, selectAll],
  ) as ColumnDef<TData, TValue>[];

  const table = useTable({
    features: dimeTableFeatures,
    data,
    columns: resolvedColumns as unknown as Parameters<
      typeof useTable<DimeTableFeatures, TData>
    >[0]['columns'],
    state: {
      sorting: resolvedSorting,
      columnFilters: resolvedColumnFilters,
      rowSelection: selected,
      columnVisibility: resolvedVisibility,
    },
    onSortingChange: resolvedOnSortingChange,
    onColumnFiltersChange: resolvedOnColumnFiltersChange,
    onColumnVisibilityChange: resolvedOnVisibilityChange,
    onRowSelectionChange: onUpdateSelected,
    getRowId: getRowId ? (row) => getRowId(row) : defaultGetRowId,
    manualSorting,
    manualFiltering,
    enableRowSelection: showSelect && (canSelectRow ? (row) => canSelectRow(row.original) : true),
    meta: { isLoading },
  });

  const visibleRowCount = table.getRowModel().rows.length;
  React.useEffect(() => {
    onVisibleRowCountChange?.(visibleRowCount);
  }, [visibleRowCount, onVisibleRowCountChange]);

  const rows = table.getRowModel().rows;
  const originals = React.useMemo(() => rows.map((row) => row.original), [rows]);
  const rowGroups = useRowGroups(originals, getRowGroupId, rowGroupsExpanded);
  const [widthRef, narrow] = useIsNarrowerThan<HTMLDivElement>(
    MOBILE_ROW_MAX_WIDTH,
    renderMobileRow !== undefined,
  );
  const mobile = renderMobileRow !== undefined && narrow;
  const toggleColumn = getRowGroupId !== undefined && !mobile;
  const colCount = resolvedColumns.length + (toggleColumn ? 1 : 0);
  const groupOf = (groupId: string) => ({
    id: groupId,
    rows: rowGroups.rowsOf(groupId),
    expanded: !rowGroups.isCollapsed(groupId),
    onToggle: () => rowGroups.toggle(groupId),
  });
  const expandAllControl =
    getRowGroupId !== undefined && rowGroups.hasGroups ? (
      <DimeButton
        type="button"
        variant="outline"
        size="sm"
        onClick={() => rowGroups.setAllExpanded(!rowGroups.allExpanded)}
      >
        {rowGroups.allExpanded ? 'Collapse' : 'Expand'} all{rowGroupNoun ? ` ${rowGroupNoun}` : ''}
      </DimeButton>
    ) : null;
  // `selected` spans every page, but getFilteredSelectedRowModel() sees only the loaded one.
  const resolvedGetRowId = getRowId ? (row: TData) => getRowId(row) : defaultGetRowId;
  const exportTable: ExportTable<TData> = showSelect
    ? {
        ...(table as unknown as ExportTable<TData>),
        getVisibleLeafColumns: () => table.getVisibleLeafColumns() as ExportableColumn<TData>[],
        getRowModel: () => table.getRowModel(),
        getSelectedRowIds: () => Object.keys(selected).filter((id) => selected[id]),
        getRowId: resolvedGetRowId,
      }
    : (table as unknown as ExportTable<TData>);
  const exportControl = showExport ? (
    <DataTableExport
      table={exportTable}
      fileName={exportFileName}
      disabledReason={exportDisabledReason}
      onRequestExportRows={onRequestExportRows}
    />
  ) : null;
  const sortControl = mobile ? (
    <DataTableCardSort table={table as unknown as CardSortTable} isLoading={isLoading} />
  ) : null;
  const showToolbar = Boolean(
    searchColumn ||
    onSearchChange ||
    filters ||
    filterChips ||
    actions ||
    expandAllControl ||
    showViewOptions ||
    showExport ||
    (mobile && table.getVisibleLeafColumns().some((column) => column.getCanSort())),
  );
  const footerRef = React.useRef<HTMLDivElement>(null);
  const {
    containerRef,
    style: heightStyle,
    isBounded,
  } = useBoundedTableHeight(maxBodyHeight, {
    reserveBelowRef: footerRef,
  });

  return (
    <div ref={widthRef} className={cn('min-w-0 w-full', className)}>
      {exportConfirmDialog}
      {showToolbar && (
        <DataTableToolbar
          table={table}
          searchColumn={searchColumn}
          searchPlaceholder={searchPlaceholder}
          searchMaxLength={searchMaxLength}
          onSearchChange={onSearchChange}
          searchValue={searchValue}
          filters={filters}
          actions={
            expandAllControl ? (
              <>
                {expandAllControl}
                {actions}
              </>
            ) : (
              actions
            )
          }
          showViewOptions={showViewOptions && !mobile}
          viewOptionsMenuClassName={viewOptionsMenuClassName}
          filterChips={filterChips}
          exportControl={exportControl}
          sortControl={sortControl}
        />
      )}
      <div ref={containerRef} className="rounded-md border border-border overflow-hidden">
        <DimeTable
          containerClassName={isBounded ? 'overflow-auto' : undefined}
          containerStyle={heightStyle}
        >
          {showHeaders && !mobile && (
            <DimeTableHeader sticky={isBounded}>
              {table.getHeaderGroups().map((headerGroup) => (
                <DimeTableRow key={headerGroup.id} className="hover:bg-transparent">
                  {toggleColumn ? (
                    <DimeTableHead className="w-12">
                      <span className="sr-only">Expand</span>
                    </DimeTableHead>
                  ) : null}
                  {headerGroup.headers.map((header) => (
                    <DimeTableHead
                      key={header.id}
                      aria-sort={getAriaSort(header.column)}
                      className={header.column.columnDef.meta?.headerClassName}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </DimeTableHead>
                  ))}
                </DimeTableRow>
              ))}
            </DimeTableHeader>
          )}
          <DimeTableBody>
            {isLoading && rows.length === 0 ? (
              <SkeletonRows rowCount={loadingRowCount} columnCount={mobile ? 1 : colCount} />
            ) : rows.length ? (
              rows.map((row, index) => {
                const groupId = rowGroups.groupIdOf(index);
                const group =
                  groupId !== null && rowGroups.startsGroup(index) ? groupOf(groupId) : null;
                const groupHeader =
                  group === null ? null : mobile ? (
                    renderMobileRowGroupHeader ? (
                      <DimeTableRow className="bg-muted/40 hover:bg-muted/40">
                        <DimeTableCell colSpan={colCount} className="whitespace-normal">
                          {renderMobileRowGroupHeader(group)}
                        </DimeTableCell>
                      </DimeTableRow>
                    ) : null
                  ) : (
                    <DimeTableRow
                      data-slot="row-group-header"
                      data-state={group.expanded ? 'open' : 'closed'}
                      className={cn(
                        'cursor-pointer',
                        group.expanded && 'bg-muted hover:bg-muted border-b-0',
                      )}
                      onClick={group.onToggle}
                    >
                      <DimeTableCell className="relative">
                        <div className="flex justify-center">
                          <RowGroupToggle
                            expanded={group.expanded}
                            label={
                              rowGroupToggleLabel?.(group) ??
                              (group.expanded ? 'Collapse group' : 'Expand group')
                            }
                            onToggle={group.onToggle}
                          />
                        </div>
                        {group.expanded ? (
                          <span
                            aria-hidden
                            className="bg-foreground/30 absolute top-[calc(50%+0.875rem)] bottom-0 left-1/2 w-0.5 -translate-x-1/2"
                          />
                        ) : null}
                      </DimeTableCell>
                      {row.getVisibleCells().map((cell) => (
                        <DimeTableCell key={cell.id}>
                          {cell.column.columnDef.meta?.groupCell?.(group as never) ?? null}
                        </DimeTableCell>
                      ))}
                    </DimeTableRow>
                  );
                if (groupId !== null && rowGroups.isCollapsed(groupId)) {
                  return <React.Fragment key={row.id}>{groupHeader}</React.Fragment>;
                }
                const subRow = renderSubRow?.(row.original) ?? null;
                return (
                  <React.Fragment key={row.id}>
                    {groupHeader}
                    <DimeTableRow
                      striped={striped && row.index % 2 === 1}
                      data-state={row.getIsSelected() ? 'selected' : undefined}
                      onClick={
                        onRowClick
                          ? (click) => {
                              if (!click.defaultPrevented) onRowClick(row.original);
                            }
                          : undefined
                      }
                      className={cn(
                        onRowClick ? 'cursor-pointer' : undefined,
                        groupId !== null && !mobile && 'bg-muted/50 hover:bg-muted',
                        groupId !== null &&
                          !mobile &&
                          !rowGroups.endsGroup(index) &&
                          'border-b-border/50',
                        getRowClassName?.(row.original),
                      )}
                    >
                      {toggleColumn ? (
                        <DimeTableCell aria-hidden className="relative">
                          {groupId === null ? null : (
                            <>
                              <span
                                className={cn(
                                  'bg-foreground/30 absolute top-0 left-1/2 w-0.5 -translate-x-1/2',
                                  rowGroups.endsGroup(index) ? 'h-1/2' : 'h-full',
                                )}
                              />
                              <span className="bg-foreground/30 absolute top-1/2 right-0 left-1/2 h-0.5 -translate-y-1/2" />
                            </>
                          )}
                        </DimeTableCell>
                      ) : null}
                      {mobile ? (
                        <DimeTableCell colSpan={colCount} className="whitespace-normal">
                          {renderMobileRow(row.original)}
                        </DimeTableCell>
                      ) : (
                        row
                          .getVisibleCells()
                          .map((cell) => (
                            <DimeTableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </DimeTableCell>
                          ))
                      )}
                    </DimeTableRow>
                    {subRow !== null && (
                      <DimeTableRow className="hover:bg-transparent">
                        <DimeTableCell
                          colSpan={
                            mobile
                              ? colCount
                              : colCount - resolvedColumns.length + row.getVisibleCells().length
                          }
                          className="whitespace-normal"
                        >
                          {subRow}
                        </DimeTableCell>
                      </DimeTableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <DimeTableRow>
                <DimeTableCell
                  colSpan={colCount}
                  className={cn(
                    'h-24 text-center',
                    typeof emptyMessage === 'string'
                      ? 'text-muted-foreground'
                      : 'whitespace-normal',
                  )}
                >
                  {emptyMessage}
                </DimeTableCell>
              </DimeTableRow>
            )}
          </DimeTableBody>
        </DimeTable>
      </div>
      <div ref={footerRef} data-slot="table-below-reserve">
        <DataTablePagination
          table={table}
          showSelect={showSelect}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
          onFirstPage={onFirstPage}
          onLastPage={onLastPage}
          showFirstLast={showFirstLast}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={onPageSizeChange}
          rowCount={getRowGroupId === undefined ? rows.length : rowGroups.pagedCount}
          totalCount={totalCount}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
